const HOTJAR_SCRIPT_VERSION = 6;
const HOTJAR_BASE_URL = "https://static.hotjar.com/c/hotjar-";

interface HotjarFn {
  (...args: unknown[]): void;
  q?: unknown[][];
}

interface HotjarSettings {
  hjid: number;
  hjsv: number;
}

interface HotjarWindow {
  hj?: HotjarFn;
  _hjSettings?: HotjarSettings;
}

const getHotjarWindow = (): Window & HotjarWindow =>
  window as Window & HotjarWindow;

/**
 * Injects and initializes Hotjar (heatmaps + session recording).
 *
 * Guards:
 * - No-op when `VITE_HOTJAR_ID` is empty (e.g. preprod/prod, where Hotjar is
 *   driven by GTM instead).
 * - No-op when `window.hj` already exists, preventing duplicate script
 *   injection if a consent event fires more than once.
 *
 * Hotjar's default data suppression (standard form inputs masked) applies out
 * of the box; no extra configuration is required here.
 *
 * @returns `true` only when this call injected the Hotjar script, so callers
 * can distinguish a self-initiated load from a no-op (empty id, or Hotjar
 * already present via GTM).
 */
const initHotjar = (): boolean => {
  const hjid = import.meta.env.VITE_HOTJAR_ID;
  if (!hjid) return false;

  const hotjarWindow = getHotjarWindow();
  if (hotjarWindow.hj) return false;

  const hj: HotjarFn = (...args: unknown[]) => {
    (hj.q = hj.q ?? []).push(args);
  };
  hotjarWindow.hj = hj;
  hotjarWindow._hjSettings = {
    hjid: Number(hjid),
    hjsv: HOTJAR_SCRIPT_VERSION
  };

  const head = document.getElementsByTagName("head")[0];
  const script = document.createElement("script");
  script.async = true;
  script.src = `${HOTJAR_BASE_URL}${hotjarWindow._hjSettings.hjid}.js?sv=${hotjarWindow._hjSettings.hjsv}`;
  head.appendChild(script);

  return true;
};

export default initHotjar;
